import React, { useCallback, useEffect, useState } from "react";
import { Download, BookOpen, AlertTriangle, ChevronRight, Workflow, Settings2, ShieldCheck, Activity, Coins, FileText, Shield, Send, Mail, CheckCircle2, RefreshCw, UserPlus, Hourglass, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import WhoCanDoWhatPanel from "./WhoCanDoWhatPanel";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const MethodologyLeadsSection = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [filter, setFilter] = useState("requests"); // requests | downloads

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/methodology-leads");
      setLeads(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load methodology requests");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendClient = async (lead) => {
    if (!window.confirm(`Email the client-safe Methodology Overview (pricing & framework references removed) to ${lead.email}?\n\nUse this only for serious prospects.`)) return;
    setSendingId(lead.id);
    try {
      const { data } = await api.post(`/admin/methodology-leads/${lead.id}/send-client-version`);
      toast.success(`Client version sent to ${data.email}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setSendingId(null); }
  };

  const isDownload = (l) => l.source === "public_paper_download";
  const requests = leads.filter((l) => !isDownload(l));
  const downloads = leads.filter(isDownload);
  const showing = filter === "downloads" ? downloads : requests;

  const Tab = ({ id, label, count }) => (
    <button type="button" onClick={() => setFilter(id)} data-testid={`methodology-leads-tab-${id}`}
      className={`text-[12px] tracking-[0.1em] uppercase px-3 py-1.5 rounded ${filter === id ? "bg-[#7737F6] text-white" : "ek-surface-2 ek-text-soft hover:bg-zinc-200"}`}>
      {label} <span className="opacity-70">({count})</span>
    </button>
  );

  return (
    <div data-testid="methodology-leads-section">
      <SectionHeading icon={Mail} title="Methodology paper pipeline" sub="Two streams: people who requested the gated paper (action: send the client-safe overview), and people who self-served the public overview (their email, if shared)." />
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex gap-2">
          <Tab id="requests" label="Paper requests" count={requests.length} />
          <Tab id="downloads" label="Paper downloads" count={downloads.length} />
        </div>
        <button type="button" onClick={load} className="ek-text-muted hover:text-black text-[12px]" data-testid="methodology-leads-refresh">
          <RefreshCw size={12} className="inline mr-1" /> Refresh
        </button>
      </div>
      {loading ? (
        <p className="ek-text-muted text-[13px]">Loading…</p>
      ) : showing.length === 0 ? (
        <div className="ek-surface border ek-line p-8 text-center">
          <p className="ek-text-soft text-[14px]">{filter === "downloads" ? "No public paper downloads with an email yet." : "No methodology paper requests yet."}</p>
        </div>
      ) : (
        <div className="overflow-x-auto ek-surface border ek-line">
          <table className="min-w-full text-[13px]">
            <thead className="ek-surface-2 text-left ek-text-muted text-[11px] uppercase tracking-[0.12em]">
              <tr>
                <th className="p-3">{filter === "downloads" ? "Downloaded" : "Requested"}</th>
                {filter === "requests" && <th className="p-3">Name</th>}
                <th className="p-3">Email</th>
                {filter === "requests" ? <th className="p-3">Organisation</th> : <th className="p-3">Keep posted</th>}
                <th className="p-3">Status</th>
                {filter === "requests" && <th className="p-3"></th>}
              </tr>
            </thead>
            <tbody>
              {showing.map((l) => {
                const sent = l.delivery_status === "client_version_sent";
                return (
                  <tr key={l.id} className="border-t ek-line hover:bg-zinc-50/50" data-testid={`methodology-lead-row-${l.id}`}>
                    <td className="p-3 whitespace-nowrap ek-text-soft">{fmtDate(l.created_at)}</td>
                    {filter === "requests" && <td className="p-3 font-medium ek-text">{l.full_name || "—"}</td>}
                    <td className="p-3 font-mono ek-text-soft">{l.email}</td>
                    {filter === "requests"
                      ? <td className="p-3 ek-text-soft">{l.organisation || "—"}</td>
                      : <td className="p-3">{l.keep_posted ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> Yes</span> : <span className="ek-text-muted">No</span>}</td>}
                    <td className="p-3">
                      {filter === "downloads" ? (
                        <span className="inline-block bg-violet-100 text-[#7737F6] text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5">Self-serve</span>
                      ) : sent ? (
                        <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5" title={fmtDate(l.client_version_sent_at)}>
                          <CheckCircle2 size={11} /> Client version sent
                        </span>
                      ) : (
                        <span className="inline-block ek-surface-2 ek-text-soft text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5">{l.delivery_status || "requested"}</span>
                      )}
                    </td>
                    {filter === "requests" && (
                      <td className="p-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => sendClient(l)}
                          disabled={sendingId === l.id}
                          className="text-[12px] text-[#7737F6] hover:text-[#5D28C7] inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`methodology-lead-send-${l.id}`}
                        >
                          <Send size={12} /> {sendingId === l.id ? "Sending…" : sent ? "Resend client version" : "Send client version"}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const RECIPES = [
  {
    icon: UserPlus,
    title: "Create a user & allocate a role",
    steps: [
      "Users tab → Invite. Enter the person's email, pick a role, and (for trial/campaign) a working-day window. An Auth0 sign-in invite is emailed; the email is also added to the allowlist so they're recognised on first login.",
      "Roles, highest to lowest: Founder (you — everything incl. user writes, master rate card, legal IP), Sales manager (read users, all pipeline, campaigns, audit, deal rate cards, pricing), Salesperson (own pipeline + run assessments), Trial user (one-off /tools assessment, time-boxed), Campaign user (single-use /tools, e.g. an event), Lite public (anonymous site visitor — never assigned manually).",
      "Allocate the least privilege that does the job. Only Founder can create/change users or see the master rate card and legal IP. Sales managers can read users but not edit them.",
      "Change a role later: Users tab → find the allowlist entry → reassign role (and trial length). Changes take effect on their next request — no redeploy.",
      "Suspend / restore: Users tab → Suspend instantly revokes all access (session + allowlist); Restore re-enables. Use Suspend the moment someone leaves.",
      "If an invite email never arrives, the Recent invitations table shows the Resend action and the Resend's message id (cross-check in Resend's dashboard).",
    ],
  },
  {
    icon: Sparkles,
    title: "Run a time-boxed prospect trial (/tools)",
    steps: [
      "Invite the prospect as a Trial user (default 5 working days, configurable) or Campaign user (single-use, e.g. a conference lead). Weekends are skipped, so a 5-day trial granted on a Thursday still spans a full working week.",
      "They sign in and land on /tools — a self-contained workspace that does NOT expose the admin dashboard. They pick a framework, answer the diagnostic, and generate a board pack / board paper.",
      "Trial outputs are watermarked ('EKSELENS360 · TRIAL COPY') and have all pricing stripped — safe to let a prospect self-serve without leaking commercials.",
      "One-off means one full assessment per prospect. After they complete it (or the window lapses) access downgrades to lite-public automatically; no manual clean-up needed.",
      "To give a high-value prospect longer, reassign their trial length in Users — no code change. To convert them, move them to a staff role or start a real engagement in Assessments.",
    ],
  },
  {
    icon: Hourglass,
    title: "Manage trials & follow up before they expire",
    steps: [
      "Trials tab → Countdown. The top 'Tool-trial prospects' widget lists invited /tools prospects by soonest expiry, with a live countdown pill and whether they've signed in. It refreshes itself every 60 seconds.",
      "An automated nudge email goes out once when a prospect has ~1 working day left ('Your evaluation ends tomorrow' → finish-assessment CTA + book-a-call). The widget shows a green 'Sent' once that's fired.",
      "Use 'Send nudges now' to fire the batch on demand, or the per-row Email button for a personal, warm follow-up — that human touch is what converts.",
      "The lower 'Trial countdown' buckets cover the separate full-trial requestors (people who used the public trial-request form): Urgent / Warm / Early / Awaiting sign-in, each with one-click Extend and Resend-magic-link.",
      "Toggle the nudge email on/off in Email settings → 'Trial — prospect 1-day nudge' if you ever want to handle outreach entirely by hand.",
    ],
  },
  {
    icon: Workflow,
    title: "Run a full engagement end-to-end",
    steps: [
      "Assessments tab → New run. Pick the sub-brand framework, enter client + sector + region + size, save.",
      "Send the indicator questionnaire link to the client team. As responses come in they flow into the run.",
      "Once ≥85% completeness — Generate Plan (Claude Opus 4.8). Background job; status flips ready in ~90s.",
      "Generate Evidence Review against the captured narratives. This challenges the self-scores; never mutates them.",
      "Generate CSI Tailoring to localise the moves to the client's sector and regulatory regime.",
      "Run any of the four exports (PDF / PPTX / DOCX / XLSX). Pricing annex is auto-included when a plan + rate card exist.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Interpret an Evidence Review",
    steps: [
      "Verdict pill — Defensible / Partially defensible / Not defensible. Credibility % is the headline number you defend in a SteerCo.",
      "By-dimension card — look for the highest INSUFFICIENT or CONTRADICTED count. That's where the evidence ask lives.",
      "Indicator findings — Δ-1 / Δ+2 means Opus's implied score from the narrative differs from the self-score by that amount. Δ is informational only; scores never change.",
      "Top evidence asks — paste these into the kick-off pack. The client should bring artefacts in priority order before the next workshop.",
      "Once a fresher review exists, the next Plan generation will be defensibility-aware automatically (validate-first tasks + ~20% budget downweight on low-credibility moves).",
    ],
  },
  {
    icon: Coins,
    title: "Edit the rate card",
    steps: [
      "Rate cards tab → pick the default card or Clone the seeded Boutique — UK Senior Delivery card.",
      "Adjust day-rates per role to match your current commercial posture. The seed sits at ~70–80% of CCS RM6263 Tier-1 bands.",
      "Use the modifier tables for region (London / regional UK / EU / US), sector (financial services / regulated utility / telecom / healthcare / public sector) and size (small / mid / large / global).",
      "Fallback rate is what Opus's unknown roles will price at. Lower fallback = stronger procurement defensibility; if it's hit the plan's cost-confidence drops one tier.",
      "Live pricing preview pane shows the per-role line-by-line breakdown against a sample resource model — confirm modifiers feel right before saving.",
      "Make-default on the card you want all new runs to pick up. Per-run overrides remain possible at generation time.",
    ],
  },
  {
    icon: Settings2,
    title: "When to regenerate vs reuse cached enrichments",
    steps: [
      "Plan / Evidence / CSI Tailoring all cache on input fingerprints — same scores + same constraints → instant cache hit (no Opus tokens spent).",
      "Cache invalidates automatically when the count of answered indicators changes (evidence review) or when the selected move set changes (CSI tailoring) or when constraints change (plan).",
      "Manual Regenerate forces a fresh Opus call. Use it after a substantive narrative re-write or after a change in client circumstances (e.g. new regulatory event).",
      "If you see status: generating on POST, it's running in background — don't double-click. The frontend polls every 4s and the UI will switch to Ready when done.",
    ],
  },
  {
    icon: Activity,
    title: "Read the Ops dashboard",
    steps: [
      "Three jobs run on a schedule: lead backup (daily 03:00 UTC), weekly stakeholder PDF (Mon 06:00 UTC), weekly SEO digest (Mon 07:00 UTC).",
      "Status pills — green/ok, red/failed, amber/running, grey/never_run. Click into the panel to see last error.",
      "Lite history table — anonymous device → sub-brand scores (links to identified portfolio_leads when a device_id was passed at lead submission).",
      "In-progress drafts — visitors who started a lite but didn't finish. Useful nurture pool — the reactivation email nudge ships in a follow-up batch.",
      "Manual triggers — Lead backup tab and Stakeholder report tab both have Run now buttons for ad-hoc runs.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Verify content & the hard QA export gate",
    steps: [
      "Enterprise360 → Curation → Authoritative content governance. Three shared sources — Standards clause mapping, Regulator submission templates, Benchmark cohort — each carry a status: Illustrative (default), Under review, or Ekselens-verified.",
      "Only the owner can mark a source 'Ekselens-verified' (or retract it). Any admin can move a source to 'Under review'. Add a reviewer note, then click Mark Ekselens-verified.",
      "Until a source is verified, packs carry a 'for guidance' caveat and the benchmark is watermarked as illustrative.",
      "The hard gate: an UNVERIFIED regulator PDF cannot be exported as authoritative. Downloading it prompts a confirmation; if you proceed it's issued with a red 'FOR GUIDANCE ONLY — NOT YET EKSELENS-VERIFIED' banner + your name, and is audit-logged. Verify the source first for a clean export.",
      "Tip: verify content before a client meeting so packs export clean, not watermarked.",
    ],
  },
  {
    icon: FileText,
    title: "Run the Standards & Regulatory engine",
    steps: [
      "Open a client → Standards & Regulatory. Standards mapping covers ISO 27001, COBIT 2019, ISO 20000-1, ITIL 4, TOGAF 10 and ISO 22301 — each with a coverage %.",
      "Regulator packs: DORA, NIS2, NHS DCB0129/0160, EU AI Act (Reg. (EU) 2024/1689) and UK Operational Resilience (FCA PS21/3 / SYSC 15A). Each requirement shows met / partial / gap, mapped live from the RAID / decisions / gates / benefits registers.",
      "Read the readiness %, then Sign off the pack (four-eyes) when ready — the reviewer + date stamps on the pack.",
      "Download the PDF (subject to the content-governance gate above).",
      "Keep the Delivery & Assurance Hub registers current — regulator readiness is derived from them automatically.",
    ],
  },
  {
    icon: Activity,
    title: "Read & interpret a benchmark",
    steps: [
      "Open a client → Benchmark. It shows where the client sits vs an anonymised cohort, gated by k-anonymity (no single engagement is identifiable; no names or £).",
      "If the cohort relies on seed data it's clearly watermarked 'illustrative — not for authoritative client comparison'.",
      "Use the 'Real contributions only' toggle to exclude illustrative seeds; if there aren't enough real contributors yet it tells you, rather than showing a padded figure.",
      "A client only joins the cohort when opted in.",
    ],
  },
  {
    icon: Coins,
    title: "Connect integrations (Microsoft 365 & QuickBooks)",
    steps: [
      "Enterprise360 → Curation / integrations. Connect Microsoft 365 → complete the Microsoft OAuth sign-in; Sync then pulls Outlook email + calendar two-way against the relationship record.",
      "Connect QuickBooks → complete the Intuit sign-in. On an engagement → billing → Push to QuickBooks creates the invoice; payment status syncs back.",
      "QuickBooks runs in SANDBOX until production keys are supplied — sandbox invoices are not real.",
      "If a sync errors, the token has likely expired — reconnect from the integrations area.",
    ],
  },
  {
    icon: Workflow,
    title: "Map SAP or Salesforce onto an engagement (Application blueprints)",
    steps: [
      "Enterprise360 → Application blueprints. Maintain the illustrative overlay mappings per platform: SAP S/4HANA, SAP SuccessFactors, or Salesforce (Sales / Service / Revenue / Data / Marketing Cloud).",
      "In a client's engagement view, open the Application-blueprints fit-gap lens, choose the platform and greenfield/brownfield basis to get a scoped backlog per value stream (boundary/cloud tag + reconfiguration effort).",
      "Click SOW PDF to export a branded, SI-ready 'fit-gap → SOW starter'. Unverified content exports with a watermark.",
      "All overlay content is ILLUSTRATIVE until an accredited platform SME marks it verified — do not issue an unverified pack to a client as authoritative.",
    ],
  },
  {
    icon: AlertTriangle,
    title: "Troubleshooting common issues",
    steps: [
      "Opus call returns 429 — LLM budget low. Top up via the Emergent platform: Profile → Universal Key → Add Balance.",
      "Plan generation stuck at generating for >5 minutes — check Ops tab for the last scheduler error, then check backend logs at /var/log/supervisor/backend.*.log.",
      "Export fails — usually a missing run or absent scores. Run completeness < 30% can't be exported. Open the run in Assessments tab and confirm scores populated.",
      "Rate card pricing says cost_confidence: low — Opus invented role names not in the card. Open Rate cards tab, add those roles, regenerate plan.",
      "Invitation email not received — check Resend dashboard for the message id stored in the Recent invitations table.",
    ],
  },
];



const SectionHeading = ({ icon: Icon, title, sub }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2">
      {Icon ? <Icon size={18} className="text-[#7737F6]" /> : null}
      <h3 className="ek-heading text-lg ek-text">{title}</h3>
    </div>
    {sub ? <p className="text-[13px] ek-text-soft mt-1">{sub}</p> : null}
  </div>
);

const Recipe = ({ recipe }) => {
  const [open, setOpen] = useState(false);
  const Icon = recipe.icon;
  return (
    <div className="border ek-line ek-surface" data-testid={`admin-help-recipe-${recipe.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-zinc-50"
      >
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-[#7737F6] shrink-0" />
          <div className="ek-heading text-[15px] ek-text">{recipe.title}</div>
        </div>
        <ChevronRight size={16} className={`ek-text-muted transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open ? (
        <ol className="px-5 pb-5 pt-1 list-decimal pl-10 space-y-2 text-[14px] ek-text-soft font-light leading-relaxed">
          {recipe.steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      ) : null}
    </div>
  );
};

const HelpPanel = ({ me } = {}) => {
  return (
    <div className="space-y-10" data-testid="admin-help-panel">
      <div>
        <div className="flex items-center gap-2">
          <BookOpen size={18} className="text-[#7737F6]" />
          <div className="ek-eyebrow text-[#7737F6] text-[11px]">Operator guide</div>
        </div>
        <h2 className="ek-heading text-2xl ek-text mt-2">How to run EKSELENS360</h2>
        <p className="text-[14px] ek-text-soft font-light mt-3 max-w-3xl">
          Workflow recipes, interpretation notes and troubleshooting for the EKSELENS360 admin. The methodology paper is a <strong>confidential, owner-only</strong> document — download it below to share with procurement and sponsors at your discretion. It is no longer publicly downloadable. A <strong>client-safe overview</strong> (pricing &amp; external framework references removed) can be emailed to serious prospects from the requests table further down.
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <a
            href={`${API_BASE}/api/admin/methodology.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="ek-btn ek-btn-primary inline-flex items-center gap-2"
            data-testid="admin-help-download-methodology-pdf"
          >
            <Download size={16} />
            Download the full paper (owner)
          </a>
          <a
            href={`${API_BASE}/api/admin/methodology-client.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="ek-btn ek-btn-outline inline-flex items-center gap-2"
            data-testid="admin-help-download-methodology-client-pdf"
          >
            <Download size={16} />
            Preview the client version
          </a>
        </div>
      </div>

      <MethodologyLeadsSection />

      <div>
        <SectionHeading icon={Workflow} title="Workflow recipes" sub="Step-by-step playbooks for the recurring engagement workflows." />
        <div className="space-y-3">
          {RECIPES.map((r) => (
            <Recipe key={r.title} recipe={r} />
          ))}
        </div>
      </div>

      <div>
        <SectionHeading icon={FileText} title="Where to find things" sub="A map of the admin surface, in case it's been a while." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[14px]">
          {[
            ["Contacts / Brochure / Resource leads / Newsletter", "Inbound enquiries and downloads."],
            ["Resources", "The public lite-assessment landing pages."],
            ["Toolkits", "IP library — methodologies, questionnaires, maturity models, workshop packs."],
            ["Assessments", "All full-engagement runs — open one to access Plan / Evidence / CSI panels and exports."],
            ["Assessment leads / Portfolio leads", "Identified inbound from the lite-assessment ↔ full-engagement bridge."],
            ["Enterprise360", "The Reinvention Blueprint engine + Delivery & Assurance Hub: Blueprint Selector, Clients, ValueStream360 (heat strip), Application blueprints (SAP/Salesforce fit-gap lens + SOW PDF), and Curation (content governance + integrations)."],
            ["CRM360 / Relationship Hub", "The commercial spine: leads → prospects → evidence-backed proposals → engagements → billing (push to QuickBooks)."],
            ["Standards & Regulatory (per client)", "Standards mapping (6 standards) + regulator packs (DORA, NIS2, NHS DCB, EU AI Act, UK Op-Res) with sign-off and the QA export gate."],
            ["Benchmark (per client)", "Anonymised, k-anonymity-gated cohort comparison with illustrative-vs-verified honesty and a real-only toggle."],
            ["Lead backup", "Daily off-Emergent XLSX backup of all 5 lead collections."],
            ["Stakeholder report", "Weekly KPIs PDF emailed Monday 06:00 UTC."],
            ["Training", "Curated learning content."],
            ["Rate cards", "The deterministic pricing engine's source of truth."],
            ["Ops", "Scheduler health + anonymous lite history + in-progress drafts."],
            ["SEO", "Search-engine health digest + sitemap status."],
            ["Users", "Invite people, allocate roles & trial windows, suspend/restore, recent invitations."],
            ["Trials", "Review queue (full-trial requests) + Countdown — incl. the 'Tool-trial prospects' expiry/nudge widget."],
            ["Email settings", "Per-category on/off switches for every transactional email (incl. the trial nudge)."],
          ].map(([k, v]) => (
            <div key={k} className="border ek-line p-4 ek-surface">
              <div className="ek-heading text-[14px] ek-text">{k}</div>
              <div className="ek-text-soft mt-1 font-light">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionHeading icon={Shield} title="Who can do what" sub="Live permission matrix — auto-generated from the backend. No manual maintenance." />
        <WhoCanDoWhatPanel />
      </div>

      <div className="bg-[#FAF7FF] border-l-2 border-[#7737F6] p-6">
        <div className="ek-eyebrow text-[#7737F6] text-[11px]">Defensibility principle</div>
        <p className="text-[14.5px] ek-text mt-2 max-w-3xl">
          LLMs propose, Ekselens IP prices. Scores and pricing are always defensible from first principles. The LLM only enriches narrative, plan and tailoring — it can never move a headline number. If you ever feel a number is wrong, the rubric / weights / rate card is where to look, not the model.
        </p>
      </div>
    </div>
  );
};

export default HelpPanel;
